import React, { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Popover from "./Popover";
import IconButton from "./IconButton";
import { Clock } from "./icons";
import type { TrailEntry } from "@/hooks/useNavigationTrail";
import type { RecentPage } from "@/hooks/useRecentPages";

/**
 * Breadcrumb - Warm Precision design-system primitive for the top-of-page
 * navigation bar.
 *
 * Renders ONE clean hierarchical trail (never history), computed fresh from
 * route config every render - there is no separate "quick back" widget with
 * its own mutable state. Crumbs are plain text/links separated by "›" -
 * no "<-" affix on any crumb (removed 2026-08-10: a bare arrow glyph baked
 * into breadcrumb text reads as a dead/decorative back button, especially
 * on a non-clickable parent crumb with no `path`; pages that need a real
 * back action should use ds/BackLink.tsx instead). The full recently-visited
 * list stays opt-in, surfaced only through the clock-icon popover - the
 * trail itself never grows or wraps based on where the user has been.
 *
 * Below the crumb row, a single quick-back line shows the ONE most recent
 * other page (same data, same order as the clock popover's top entry) so
 * getting back to where you just were doesn't require opening the popover
 * at all - added 2026-09-08 after the clock icon alone read as too
 * disconnected from the breadcrumb itself for that single common case.
 * Suppressed whenever that page is the current page (real URL, not just the
 * trail's menu-config path - a parameterized detail route like
 * /chat_window/42 sits under the same trail entry as /chat_window itself)
 * or is already shown as a crumb above it, to avoid a dead or redundant link.
 *
 * A crumb whose `path` is undefined (the menu config has no genuine
 * distinct target for it - e.g. a section whose only page IS the page
 * you're on) renders as plain, non-clickable text rather than a dead
 * same-URL link.
 *
 * Pattern:
 *   <Breadcrumb
 *     trail={trail}
 *     recentPages={recentPages}
 *     onClearRecentPages={clearRecentPages}
 *   />
 */

interface BreadcrumbProps {
  trail: TrailEntry[];
  recentPages?: RecentPage[];
  onClearRecentPages?: () => void;
  className?: string;
}

function timeAgo(ts: number): string {
  const diffMs = Date.now() - ts;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function Breadcrumb({
  trail,
  recentPages,
  onClearRecentPages,
  className = ""
}: BreadcrumbProps) {
  const clockRef = useRef<HTMLButtonElement>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Recent pages excluding the current page. Filtered on BOTH the trail's
  // last-entry path (the menu config's href, e.g. "/chat_window") AND the
  // real current pathname (e.g. "/chat_window/42") — a parameterized detail
  // route matches its non-`end` parent sublink in the trail, so the two can
  // legitimately differ, and either one matching the current location means
  // it's not "other". Filtering on the trail path alone let the clock
  // popover (and, worse, the always-visible quick-back line below) offer a
  // dead self-link on the very page you're on.
  const currentPath = trail[trail.length - 1]?.path;
  const otherRecentPages = (recentPages ?? []).filter(
    (p) => p.path !== currentPath && p.path !== pathname
  );

  // A single-entry trail (e.g. a collapsed self-referential section like
  // "Rules") has no hierarchy worth showing as crumbs - rendering it anyway
  // would just restate the page's own title, the exact redundancy the
  // collapse was meant to remove. But the bar is also what carries the
  // recently-visited clock popover, so it must still render (crumbs
  // suppressed, clock only) whenever there IS browsing history to show -
  // otherwise a page's recent-pages access silently disappears the moment
  // its trail happens to collapse to one crumb. An empty trail with no
  // history has nothing to show at all.
  const showCrumbs = trail.length > 1;
  if (!showCrumbs && otherRecentPages.length === 0) return null;

  // Same entry the clock popover lists first - most-recently-visited other
  // page - but only for the standalone quick-back line: skip it when that
  // page is already shown as a crumb above (e.g. the immediate parent
  // section), which would just render the same destination twice.
  const crumbPaths = new Set(trail.map((c) => c.path).filter(Boolean));
  const lastPage = otherRecentPages.find((p) => !crumbPaths.has(p.path));

  return (
    <div className={`shrink-0 flex flex-col gap-0.5 ${className}`}>
      <nav
        aria-label="breadcrumb"
        className="flex items-center justify-between gap-2 text-xs text-zinc-400"
      >
        <div className="flex items-center gap-1 min-w-0">
          {showCrumbs &&
            trail.map((crumb, i) => {
              const isLast = i === trail.length - 1;
              return (
                // Keyed by position, not crumb.path: parent crumbs without their own
                // `to` (most menu sections) have no path at all now, which can
                // collide across multiple crumbs and corrupt the render under a
                // path-based key (duplicate "Integrations" text on
                // /integrations/hubspot, missing separators).
                <React.Fragment key={i}>
                  {i > 0 && (
                    <span aria-hidden="true" className="text-zinc-300 mx-0.5">
                      ›
                    </span>
                  )}
                  {isLast ? (
                    <span
                      aria-current="page"
                      className="text-zinc-600 font-medium truncate"
                    >
                      {crumb.label}
                    </span>
                  ) : crumb.path ? (
                    <Link
                      to={crumb.path}
                      className="flex items-center gap-1 text-zinc-400 hover:text-primary transition-colors"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="flex items-center gap-1 text-zinc-400 truncate">
                      {crumb.label}
                    </span>
                  )}
                </React.Fragment>
              );
            })}
        </div>

        {otherRecentPages.length > 0 && (
          <>
            <IconButton
              ref={clockRef}
              aria-label="Recently visited pages"
              size="sm"
              variant="ghost"
              aria-haspopup="true"
              aria-expanded={historyOpen}
              onClick={() => setHistoryOpen((o) => !o)}
              className="shrink-0"
            >
              <Clock className="w-[15px] h-[15px]" />
            </IconButton>
            <Popover
              open={historyOpen}
              onClose={() => setHistoryOpen(false)}
              anchor={clockRef}
              placement="bottom-end"
              minWidth={260}
            >
              <Popover.Label>Recently visited</Popover.Label>
              {otherRecentPages.map((page) => (
                <Popover.Item
                  key={page.path}
                  onClick={() => {
                    setHistoryOpen(false);
                    navigate(page.path);
                  }}
                >
                  <span className="flex flex-col gap-0.5 w-full min-w-0">
                    <span className="truncate font-medium text-zinc-800">
                      {page.label}
                    </span>
                    <span className="truncate text-[11px] text-zinc-400">
                      {page.section ? `${page.section} — ` : ""}
                      {timeAgo(page.visitedAt)}
                    </span>
                  </span>
                </Popover.Item>
              ))}
              {onClearRecentPages && (
                <>
                  <Popover.Divider />
                  <Popover.Item
                    onClick={() => {
                      onClearRecentPages();
                      setHistoryOpen(false);
                    }}
                  >
                    Clear history
                  </Popover.Item>
                </>
              )}
            </Popover>
          </>
        )}
      </nav>
      {lastPage && (
        <Link
          to={lastPage.path}
          className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-primary transition-colors w-fit"
        >
          <span aria-hidden="true">←</span>
          <span className="truncate">
            Back to <span className="font-medium">{lastPage.label}</span>
            {lastPage.section ? ` (${lastPage.section})` : ""}
          </span>
        </Link>
      )}
    </div>
  );
}

export default Breadcrumb;
